import React from "react";
import {useNavigate} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faMapLocationDot, faListCheck, faRoute, faTerminal, faClock, faChartLine} from "@fortawesome/free-solid-svg-icons";
import {hasPermission} from "../../utils/rolePermissions";

/**
 * Per-device-type quick-access bar — surfaces the pages relevant to the selected device type.
 * Every type carries at least one action so the bar never collapses to an empty gap (which read
 * as "unfinished" when only GPS Lock had actions). Each action is gated by the same permission
 * that guards its route, so access is unchanged — only the entry point moved.
 *
 * Device-specific operations that act on ONE lock (Unlock, Schedule Unlock) live on the device
 * details panel instead — see DeviceDetailsPanel "Control Actions".
 */
const ACTIONS_BY_TYPE = {
  GPS_LOCK: [
    {label: "Live Tracking", icon: faMapLocationDot, path: "/tracking", perm: "sidebar_tracking"},
    {label: "Tasks", icon: faListCheck, path: "/tasks", perm: "sidebar_tasks"},
    {label: "Trips", icon: faRoute, path: "/trips", perm: "sidebar_trips"},
    {label: "Command Center", icon: faTerminal, path: "/command-center", perm: "sidebar_commandCenter"},
    {label: "Scheduled Unlocks", icon: faClock, path: "/scheduled-unlocks", perm: "sidebar_scheduledUnlocks"},
  ],
  // GPS Tracker (incl. FT920): Command Center now serves tracker downlinks; Reports has the
  // GT06 tabs (Trips, Daily Activity, Alert Log, Fleet Distance).
  GT06_TRACKER: [
    {label: "Command Center", icon: faTerminal, path: "/command-center", perm: "sidebar_commandCenter"},
    {label: "Reports", icon: faChartLine, path: "/reports", perm: "sidebar_reports"},
  ],
  // MobiTrack: its live-location history and visit reports live under Reports.
  MOBI_TRACK: [
    {label: "Reports", icon: faChartLine, path: "/reports", perm: "sidebar_reports"},
  ],
  // BT Lock is Bluetooth-only (no live tracking / scheduling); its lock-unlock reports are here.
  BT_LOCK: [
    {label: "Reports", icon: faChartLine, path: "/reports", perm: "sidebar_reports"},
  ],
};

export default function DeviceTypeActions({activeDeviceTab}) {
  const navigate = useNavigate();
  const actions = (ACTIONS_BY_TYPE[activeDeviceTab] || []).filter((a) => hasPermission(a.perm));

  if (actions.length === 0) return null;

  return (
    <div className="device-type-actions">
      <span className="device-type-actions-label">Quick actions:</span>
      {actions.map((a) => (
        <button
          key={a.path}
          type="button"
          className="device-type-action-btn"
          onClick={() => navigate(a.path)}
        >
          <FontAwesomeIcon icon={a.icon} />
          {a.label}
        </button>
      ))}
    </div>
  );
}
